import React from 'react';
import { Facebook, Mail, Phone, MapPin } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-gradient-to-b from-gray-900 to-black text-white">
      <div className="container mx-auto px-4 py-10 md:py-16">
        {/* Company Info - Full width on top */}
        <div className="mb-8 md:mb-0 md:float-left md:w-1/3 md:pr-8">
          <div className="flex items-center space-x-2 mb-3">
            <img
              src="/logo-glf.png"
              alt="Groupe Lavage Fenêtres"
              className="h-10 w-auto"
              data-testid="footer-logo"
            />
            <h3 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
              Groupe Lavage Fenêtres
            </h3>
          </div>
          <p className="text-gray-400 text-sm mb-4 leading-relaxed">
            Votre référence en lavage de fenêtres au Québec. Osmose écologique, soft wash et lavage à pression.
          </p>
          <div className="flex space-x-3">
            <a
              href="https://facebook.com"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white/10 hover:bg-cyan-600 rounded-full p-2.5 transition-colors"
            >
              <Facebook size={18} />
            </a>
            <a
              href="mailto:groupelavagefenetre@gmail.com"
              className="bg-white/10 hover:bg-cyan-600 rounded-full p-2.5 transition-colors"
            >
              <Mail size={18} />
            </a>
          </div>
        </div>

        {/* Clear float for desktop layout */}
        <div className="hidden md:grid md:grid-cols-2 md:float-right md:w-2/3 gap-8">
          {/* Quick Links */}
          <div>
            <h4 className="text-base font-bold mb-4 text-white">Liens rapides</h4>
            <ul className="space-y-2.5">
              <li>
                <button onClick={() => scrollToSection('hero')} className="text-gray-400 hover:text-cyan-400 transition-colors text-sm">
                  Accueil
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('gallery')} className="text-gray-400 hover:text-cyan-400 transition-colors text-sm">
                  Galerie
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('testimonials')} className="text-gray-400 hover:text-cyan-400 transition-colors text-sm">
                  Témoignages
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('contact')} className="text-gray-400 hover:text-cyan-400 transition-colors text-sm">
                  Contact
                </button>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-base font-bold mb-4 text-white">Contactez-nous</h4>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <Phone className="text-cyan-400 flex-shrink-0 mt-0.5" size={16} />
                <div>
                  <a href="tel:8192002015" className="text-gray-400 hover:text-cyan-400 transition-colors text-sm">819.200.2015</a>
                  <p className="text-xs text-gray-500">7 jours sur 7</p>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <Mail className="text-cyan-400 flex-shrink-0 mt-0.5" size={16} />
                <div>
                  <a href="mailto:groupelavagefenetre@gmail.com" className="text-gray-400 hover:text-cyan-400 transition-colors text-sm break-all">
                    groupelavagefenetre@gmail.com
                  </a>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <MapPin className="text-cyan-400 flex-shrink-0 mt-0.5" size={16} />
                <div>
                  <p className="text-gray-400 text-sm">Centre du Québec et environs</p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Mobile layout: 2-column grid */}
        <div className="grid grid-cols-2 gap-6 md:hidden">
          {/* Quick Links - Left */}
          <div>
            <h4 className="text-sm font-bold mb-3 text-white">Liens rapides</h4>
            <ul className="space-y-2">
              <li>
                <button onClick={() => scrollToSection('hero')} className="text-gray-400 hover:text-cyan-400 transition-colors text-sm">
                  Accueil
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('gallery')} className="text-gray-400 hover:text-cyan-400 transition-colors text-sm">
                  Galerie
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('testimonials')} className="text-gray-400 hover:text-cyan-400 transition-colors text-sm">
                  Témoignages
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('contact')} className="text-gray-400 hover:text-cyan-400 transition-colors text-sm">
                  Contact
                </button>
              </li>
            </ul>
          </div>

          {/* Contact Info - Right */}
          <div>
            <h4 className="text-sm font-bold mb-3 text-white">Contactez-nous</h4>
            <ul className="space-y-2.5">
              <li className="flex items-start space-x-2">
                <Phone className="text-cyan-400 flex-shrink-0 mt-0.5" size={14} />
                <a href="tel:8192002015" className="text-gray-400 text-sm">819.200.2015</a>
              </li>
              <li className="flex items-start space-x-2">
                <Mail className="text-cyan-400 flex-shrink-0 mt-0.5" size={14} />
                <a href="mailto:groupelavagefenetre@gmail.com" className="text-gray-400 text-xs break-all">
                  groupelavagefenetre@gmail.com
                </a>
              </li>
              <li className="flex items-start space-x-2">
                <MapPin className="text-cyan-400 flex-shrink-0 mt-0.5" size={14} />
                <p className="text-gray-400 text-sm">Centre du Québec</p>
              </li>
            </ul>
          </div>
        </div>

        {/* Clear floats */}
        <div className="clear-both"></div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="container mx-auto px-4 py-4 md:py-6">
          <p className="text-gray-500 text-xs md:text-sm text-center">
            © {currentYear} Groupe Lavage Fenêtres. Tous droits réservés.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
